<script lang="ts" setup>
import type { PropType } from 'vue';
import MenuItem from './MenuItem.vue'; // Adjust path if needed
import BButton from '@/components/common/bButton.vue'; // Import if needed for return button
import IcRoundKeyboardArrowLeft from '~icons/ic/round-keyboard-arrow-left'; // Import icon if needed
import { useUserStore } from '@/config/store/userStore';
import { computed, useTemplateRef } from 'vue';
import { getDefaultManagementHome } from '@/config/router/navigationsGuards/guardManagement';
import { useMagneticHighlight } from '@/composables/navigationDrawer/useMagneticHighlight';

const userStore = useUserStore();

const listRef = useTemplateRef<HTMLElement>('list');
const { style: highlightStyle } = useMagneticHighlight(listRef, '.menu-item, .menu-header');

const isAdminUser = computed(() => {
  return userStore.user?.role === 'admin' && !userStore.user?._firmId;
});

// Admin users return to management area, others to Home
const returnRoute = computed(() => {
  return isAdminUser.value ? getDefaultManagementHome(userStore.user) : { name: 'Home' };
});

defineProps({
  menuItems: {
    required: true,
  },
  isSuperAdminRoute: {
    type: Boolean,
    default: false,
  },
  // Function passed down
  closeMobileMenuFn: {
      type: Function as PropType<() => void>,
      required: true
  }
});

// No emit needed here, calls function from prop

const getMenuItemKey = (menuItem: Record<string, any>, index: number) => {
  if (menuItem.route) return `route-${menuItem.route}`
  if (menuItem.link) return `link-${menuItem.link}`
  if (menuItem.moduleId || menuItem.display) return `module-${menuItem.moduleId || 'item'}-${menuItem.display || index}-${index}`
  return `menu-item-${index}`
}
</script>

<template>
  <div
    ref="list"
    class="relative overflow-y-auto overflow-x-hidden w-full transition-all duration-200 transform mini-scroll grow"
    :class="[
        // Mobile specific height and positioning (relative to parent's state)
        'md:relative md:h-auto md:translate-y-0', // Desktop reset
    ]"
  >
    <span class="menu-highlight" :style="highlightStyle" aria-hidden="true" />
    <transition
      enter-from-class="-translate-x-1/3 opacity-0"
      enter-active-class="transition duration-300"
      leave-active-class="transition duration-300"
      mode="out-in"
    >
      <ul :class="isSuperAdminRoute ? 'flex flex-col pb-4 space-y-2' : 'flex flex-col py-2'">
         <!-- Render Return to App button if in Super Admin -->
         <li v-if="isSuperAdminRoute">
            <BButton
                :to="returnRoute"
                bg="transparent"
                class="justify-start! gap-2 tracking-wide flex uppercase whitespace-nowrap menu-item-return"
                @click="closeMobileMenuFn"
            >
              <span class="inline-flex justify-center items-center mr-2">
                <IcRoundKeyboardArrowLeft />
              </span>
              <p class="menu-item-show-hover md:opacity-0">
                Return to app
              </p>
            </BButton>
         </li>

        <!-- Render Menu Items -->
        <template 
          v-for="(menuItem, index) in menuItems"
          :key="getMenuItemKey(menuItem, index)">

          <MenuItem v-if="!menuItem.separator" 
          :menu-item="menuItem"
          @click="closeMobileMenuFn" 
          />
          <div class="mt-3" v-else></div>
        </template>
      </ul>
    </transition>
  </div>
</template>

<style scoped>
/* Apply hover/fixed styles to the return button text */
.drawer-container:is(:hover, :has(:focus-visible)) .menu-item-return .menu-item-show-hover,
.drawer-container-fixed .menu-item-return .menu-item-show-hover {
  opacity: 1;
  display: flex;
  transition: opacity 0.3s ease-in-out;
}
/* mini-scroll styles should be in main component or global */

/* The hovered (or keyboard-focused) item's background, gliding from one item to the next. */
.menu-highlight {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  border-left: 4px solid var(--tone);
  background-color: color-mix(in oklab, var(--tone) 25%, transparent);
  transition-property: transform, width, height, opacity, background-color, border-color;
  transition-duration: 0.2s;
  transition-timing-function: cubic-bezier(0.23, 1, 0.32, 1);
}

@media (prefers-reduced-motion: reduce) {
  .menu-highlight {
    transition-property: opacity;
  }
}
</style>
